import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Filter,
} from 'lucide-react';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import useWalletData from '../../hooks/useWalletData';

const formatCurrency = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value || 0));

const formatDate = (timestamp, t) => {
  if (!timestamp) return t('common.notAvailable');
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AllTransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { transactions, loading } = useWalletData(true);
  const [filter, setFilter] = useState('all');

  const filteredTransactions = useMemo(() => {
    if (filter === 'all') return transactions;
    return transactions.filter(transaction => transaction.type === filter);
  }, [transactions, filter]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="pt-32 pb-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/wallet')}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-slate-400 text-sm font-semibold mb-6 hover:text-gray-900 dark:hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('wallet.allTransactions.backToWallet')}
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-gray-900 dark:text-white">
                {t('wallet.allTransactions.title')}
              </h1>
              <p className="text-gray-500 dark:text-slate-400">
                {t('wallet.allTransactions.subtitle')}
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {['all', 'deposit', 'withdrawal', 'earning', 'payment'].map(
                option => (
                  <button
                    key={option}
                    onClick={() => setFilter(option)}
                    className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold capitalize transition ${
                      filter === option
                        ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                        : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:border-yellow-300 dark:hover:border-yellow-500'
                    }`}
                  >
                    {option === 'all' && <Filter className="h-4 w-4" />}
                    {t(`wallet.allTransactions.filter.${option}`)}
                  </button>
                ),
              )}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-100 dark:border-slate-800">
          {loading ? (
            <div className="py-12 text-center text-gray-500 dark:text-slate-400">
              {t('common.loading')}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-slate-400">
              {t('wallet.allTransactions.noTransactions')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-slate-800 text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-slate-400 uppercase tracking-wider text-xs">
                    <th className="py-3">
                      {t('wallet.allTransactions.table.description')}
                    </th>
                    <th className="py-3">
                      {t('wallet.allTransactions.table.type')}
                    </th>
                    <th className="py-3">
                      {t('wallet.allTransactions.table.amount')}
                    </th>
                    <th className="py-3">
                      {t('wallet.allTransactions.table.status')}
                    </th>
                    <th className="py-3">
                      {t('wallet.allTransactions.table.date')}
                    </th>
                    <th className="py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                  {filteredTransactions.map(transaction => {
                    const positive =
                      transaction.type === 'deposit' ||
                      transaction.type === 'earning';
                    return (
                      <tr
                        key={transaction.id}
                        className="text-gray-900 dark:text-white"
                      >
                        <td className="py-4">
                          <div className="font-semibold">
                            {transaction.description ||
                              t('wallet.walletActivity')}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">
                            {transaction.id}
                          </div>
                        </td>
                        <td className="py-4 capitalize">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              positive
                                ? 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                : 'bg-rose-100 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400'
                            }`}
                          >
                            {transaction.type === 'deposit' ? (
                              <ArrowUpRight className="h-3 w-3" />
                            ) : transaction.type === 'withdrawal' ? (
                              <ArrowDownRight className="h-3 w-3" />
                            ) : (
                              <CreditCard className="h-3 w-3" />
                            )}
                            {t(
                              `wallet.allTransactions.transactionType.${transaction.type}`,
                              {
                                defaultValue: transaction.type,
                              },
                            )}
                          </span>
                        </td>
                        <td className="py-4 font-black">
                          <span
                            className={
                              positive
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-500 dark:text-rose-400'
                            }
                          >
                            {positive ? '+' : '-'}
                            {formatCurrency(Math.abs(transaction.amount || 0))}
                          </span>
                        </td>
                        <td className="py-4 text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">
                          {transaction.status || t('wallet.statusCompleted')}
                        </td>
                        <td className="py-4 text-gray-500 dark:text-slate-400">
                          {formatDate(transaction.date, t)}
                        </td>
                        <td className="py-4 text-right">
                          <button
                            className="text-sm font-semibold text-yellow-500 dark:text-yellow-400 hover:text-yellow-400 dark:hover:text-yellow-300"
                            onClick={() =>
                              navigate(
                                `/wallet/transactions/${transaction.id}`,
                                {
                                  state: { transaction },
                                },
                              )
                            }
                          >
                            {t('wallet.allTransactions.details')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
